import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";
import { QuoteForm } from "../components/QuoteForm";
import { FinalCta } from "../components/FinalCta";
import { BUSINESS } from "../content";
import { CalendarCheck, MessageSquare, BadgeCheck, Repeat, FileText, Home } from "lucide-react";

const BENEFITS = [
  { icon: CalendarCheck, title: "Reliable Scheduling", text: "We turn up when expected, and cleans are scheduled around tenancy timelines and inspections." },
  { icon: MessageSquare, title: "Clear Communication", text: "Straightforward updates and easy contact — you'll always know where things stand." },
  { icon: BadgeCheck, title: "Quality Workmanship", text: "Detailed cleans that leave properties presentable for tenants, inspections and sale campaigns." },
  { icon: Repeat, title: "Ongoing Relationships", text: "We focus on long-term arrangements with agencies and property managers, not one-off transactions." },
  { icon: FileText, title: "Professional Invoicing", text: "Clear, professional invoicing for your records where applicable." },
  { icon: Home, title: "Flexible Requirements", text: "From vacant properties to tenant changeovers, cleaning can be tailored to each property." },
];

const SERVICES = [
  "Routine property cleaning", "End-of-lease cleaning", "Vacant property cleaning",
  "Pre-inspection cleaning", "Pre-sale cleaning", "Property presentation", "Ongoing agency work",
];

export const PropertyManagersPage = () => (
  <>
    <Seo
      title="Property Management & Real Estate Cleaning Melbourne South East | Pure Bliss Clean"
      description="A reliable cleaning partner for property managers and real estate agencies across Melbourne's South East — vacates, inspections, presentation cleans and ongoing arrangements."
      path="/property-managers"
    />
    <PageHero
      eyebrow="Property Managers & Real Estates"
      titleLines={["A Reliable Cleaning", "Partner For Your", "Properties"]}
      intro="Managing properties means relying on contractors who communicate clearly, turn up when expected and complete the job properly. Pure Bliss Clean provides dependable cleaning support for rental properties, vacates, inspections and ongoing property requirements."
      image="/assets/work/cooktop-after.webp"
      imageAlt="Inspection-ready kitchen cooktop after a Pure Bliss Clean"
    />

    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
      <Reveal className="max-w-2xl">
        <Eyebrow>Why Agencies Work With Us</Eyebrow>
        <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl">Built For Property Professionals</h2>
        <p className="mt-5 text-base leading-relaxed text-slate-600">
          We already provide ongoing scheduled cleaning services across residential, real estate and care
          environments — including regular weekly arrangements. We understand what property managers need from a
          cleaning contractor.
        </p>
      </Reveal>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {BENEFITS.map((b, i) => (
          <Reveal key={b.title} delay={0.07 * i}>
            <div className="h-full rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-teal-900/5">
              <div className="inline-flex rounded-2xl bg-teal-50 p-3.5 text-brand-teal">
                <b.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-display text-lg font-bold text-brand-ink">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <ul className="mt-12 flex flex-wrap gap-3">
          {SERVICES.map((s) => (
            <li key={s} className="rounded-full border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-bold text-brand-teal">{s}</li>
          ))}
        </ul>
      </Reveal>
    </section>

    <section className="bg-brand-mist">
      <div className="mx-auto max-w-3xl px-6 py-24 lg:py-32">
        <Reveal className="mb-10 text-center">
          <div className="flex justify-center"><Eyebrow>Agency Enquiries</Eyebrow></div>
          <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl">Discuss Your Cleaning Requirements</h2>
          <p className="mx-auto mt-4 max-w-md text-slate-600">
            Tell us about your portfolio and what you need across {BUSINESS.region}, and we'll be in touch.
          </p>
        </Reveal>
        <QuoteForm variant="property" defaultService="Property Management" source="property_managers_page" />
      </div>
    </section>
    <FinalCta />
  </>
);
